import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchData, mutateData } from '../../api/client';
import i18n from '../../i18n';

interface LocalePreferences {
  locale: string;
  currency: string;
  dateFormat: string;
  numberFormat: string;
  timezone: string;
}

interface LocaleResponse {
  preferences: LocalePreferences;
  available: {
    locales: string[];
    currencies: string[];
    dateFormats: string[];
    numberFormats: string[];
    timezones: string[];
  };
}

export function useLocale() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['config', 'locale'],
    queryFn: () => fetchData<LocaleResponse>('/api/config/locale'),
    staleTime: 5 * 60 * 1000,
  });

  // Sync i18n language with server preference
  useEffect(() => {
    const serverLocale = data?.preferences?.locale;
    if (serverLocale && i18n.language !== serverLocale) {
      i18n.changeLanguage(serverLocale);
    }
  }, [data?.preferences?.locale]);

  const updatePreferences = useMutation({
    mutationFn: (prefs: Partial<LocalePreferences>) =>
      mutateData('/api/auth/me', 'PUT', prefs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', 'locale'] });
    },
  });

  const formatCurrency = (amount: number, currencyOverride?: string): string => {
    const locale = data?.preferences?.numberFormat ?? 'sv-SE';
    const currency = currencyOverride ?? data?.preferences?.currency ?? 'SEK';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatCurrencyCompact = (amount: number, currencyOverride?: string): string => {
    const cur = currencyOverride ?? data?.preferences?.currency ?? 'SEK';
    if (Math.abs(amount) >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M ${cur}`;
    if (Math.abs(amount) >= 10_000) return `${(amount / 1_000).toFixed(0)}k ${cur}`;
    return formatCurrency(amount, cur);
  };

  const formatDate = (date: string | Date): string => {
    const tz = data?.preferences?.timezone ?? 'Europe/Stockholm';
    const fmt = data?.preferences?.dateFormat ?? 'YYYY-MM-DD';
    const d = new Date(date);

    const options: Intl.DateTimeFormatOptions = { timeZone: tz };
    if (fmt === 'YYYY-MM-DD') {
      return d.toLocaleDateString('sv-SE', { ...options, year: 'numeric', month: '2-digit', day: '2-digit' });
    } else if (fmt === 'DD/MM/YYYY') {
      return d.toLocaleDateString('en-GB', { ...options, year: 'numeric', month: '2-digit', day: '2-digit' });
    } else if (fmt === 'MM/DD/YYYY') {
      return d.toLocaleDateString('en-US', { ...options, year: 'numeric', month: '2-digit', day: '2-digit' });
    }
    return d.toLocaleDateString('sv-SE', options);
  };

  const formatNumber = (num: number): string => {
    const locale = data?.preferences?.numberFormat ?? 'sv-SE';
    return new Intl.NumberFormat(locale).format(num);
  };

  return {
    preferences: data?.preferences ?? null,
    available: data?.available ?? null,
    isLoading,
    formatCurrency,
    formatCurrencyCompact,
    formatDate,
    formatNumber,
    updatePreferences,
  };
}
